import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Plus, 
  Edit, 
  Trash2, 
  Calendar,
  MapPin,
  Building2,
  X
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const ExperienceManagement = () => {
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm();

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      const response = await api.get('/api/profile/experience/');
      console.log('Experience response:', response.data);
      
      // Handle different response structures
      const experiencesData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setExperiences(experiencesData);
    } catch (error) {
      toast.error('Failed to fetch work experience records');
      console.error('Error fetching experiences:', error);
      // Set empty array as fallback
      setExperiences([]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (editingId) {
        await api.patch(`/api/profile/experience/${editingId}/`, data);
        toast.success('Work experience updated successfully');
      } else {
        await api.post('/api/profile/experience/', data);
        toast.success('Work experience added successfully');
      }
      
      reset();
      setShowForm(false);
      setEditingId(null);
      fetchExperiences();
    } catch (error) {
      toast.error(editingId ? 'Failed to update work experience' : 'Failed to add work experience');
      console.error('Error saving experience:', error);
    }
  };

  const handleEdit = (experience) => {
    setEditingId(experience.id);
    setValue('company', experience.company);
    setValue('position', experience.position);
    setValue('location', experience.location);
    setValue('start_date', experience.start_date);
    setValue('end_date', experience.end_date);
    setValue('description', experience.description);
    setValue('is_current', experience.is_current);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this work experience record?')) {
      return;
    }

    try {
      await api.delete(`/api/profile/experience/${id}/`);
      toast.success('Work experience deleted successfully');
      fetchExperiences();
    } catch (error) {
      toast.error('Failed to delete work experience');
      console.error('Error deleting experience:', error);
    }
  };

  const handleCancel = () => {
    reset();
    setShowForm(false);
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Work Experience</h1>
              <button
                onClick={() => setShowForm(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add Experience</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {experiences.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No work experience records</h3>
                <p className="mt-1 text-sm text-gray-500">Add your work experience to showcase your professional background.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn-primary"
                  >
                    Add Experience
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {Array.isArray(experiences) && experiences.map((experience) => (
                  <div key={experience.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Briefcase className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {experience.position}
                            </h3>
                            <p className="text-sm text-gray-600">{experience.company}</p>
                          </div>
                        </div>
                        
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2" />
                            {experience.start_date} - {experience.is_current ? 'Present' : experience.end_date}
                          </div>
                          {experience.location && (
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="w-4 h-4 mr-2" />
                              {experience.location}
                            </div>
                          )}
                        </div>
                        
                        {experience.description && (
                          <p className="mt-3 text-sm text-gray-700">{experience.description}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(experience)}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Edit experience"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(experience.id)}
                          className="p-2 text-red-400 hover:text-red-600 transition-colors"
                          title="Delete experience"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Experience Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingId ? 'Edit Work Experience' : 'Add Work Experience'}
                </h3>
                <button
                  onClick={handleCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company *
                    </label>
                    <input
                      {...register('company', { required: 'Company is required' })}
                      type="text"
                      className={`input-field ${errors.company ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="Google Inc."
                    />
                    {errors.company && (
                      <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Position *
                    </label>
                    <input
                      {...register('position', { required: 'Position is required' })}
                      type="text"
                      className={`input-field ${errors.position ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="Software Engineer"
                    />
                    {errors.position && (
                      <p className="mt-1 text-sm text-red-600">{errors.position.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      {...register('location')}
                      type="text"
                      className="input-field"
                      placeholder="Mountain View, CA"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      {...register('start_date', { required: 'Start date is required' })}
                      type="date"
                      className={`input-field ${errors.start_date ? 'border-red-300 focus:ring-red-500' : ''}`}
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      {...register('end_date')}
                      type="date"
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={4}
                    className="input-field"
                    placeholder="Describe your responsibilities, achievements, and key projects..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    {...register('is_current')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    I am currently working here
                  </label>
                </div>

                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingId ? 'Update Experience' : 'Add Experience'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperienceManagement;
